import { Match, Show, Switch, Suspense, batch, createSignal, lazy, onCleanup } from "solid-js";
import { useHeroForm } from "../contexts/HeroFormContext";
import { ModalV2 } from "../components/Modal";
import { ValidationStatus } from "../components/ValidationBar";
import { getCurrentFormCompletionStatus, getFirstInvalidMeasurementCursor } from "../js/variantsUtil";

// The measurement form, its footer and the virtual keyboard only exist inside the "Edit
// Measurements" modal, which opens on click; keep them out of the hero's initial chunk.
const DIYBody = lazy(() => import("./DIYBody").then(module => ({ default: module.DIYBody })));
const DIYFooterV2 = lazy(() => import("./DIYFooter").then(module => ({ default: module.DIYFooterV2 })));
const VirtualKeyboard = lazy(() => import("../components/VirtualKeyboard").then(module => ({ default: module.VirtualKeyboard })));

export const DIYModal = (props) => {
  const [modalState, setModalState] = createSignal(false);

  const handleModalOpenState = (state) => {
    const body = document.getElementsByTagName('body')[0];
    body.style.overflow = state ? 'hidden' : '';

    setModalState(state);
  }

  onCleanup(() => {
    document.body.style.overflow = "";
  })


  const {
    sizeSettings: {
      diyData,
      measurementStatus,
      measurementsInfo,
      MEAS_VALIDATION_STATUS,
      measHtmVisibility,
      diyFormUtils: {
        measurementSchema,
        formCursor,
        formSequence,
        formDisplayValues,
        handlers: {
          setMeasurementSchema,
          setFormCursor,
          switchDisplayValueUnitsTo
        }
      },
      handlers: {
        handleMeasurementChange,
        handleMeasurementStatusChange,
        handleHtmVisibility
      }
    },
    constants: {
      FORM_COMPLETION_STATUS
    },
    sizeSettings,
    unit: {
      currentUnit,
      handlers: {
        toggleCurrentUnit
      }
    },
    navBarData: {
      handlers: {
        handleNavBarVisiblity
      }
    }
  } = useHeroForm()

  const measurements = () => {
    return Object.values(diyData().measurements).filter(measurement => measurement.isEnabled)
  }

  let formDisplayReferences = { 'header': props.menuOptionsRef }

  const addFormViewReference = (key, ref) => {
    formDisplayReferences[key] = ref;
  }

  const isInputValid = () => {
    const measurementId = formSequence()[formCursor()];
    const { VALID } = MEAS_VALIDATION_STATUS;
    return measurementStatus()[measurementId] === VALID
  }


  const handleCursorChange = (index) => {
    if (index < formSequence().length - 1 && index >= 0 && formCursor() != index) {
      const element = formDisplayReferences[formSequence()[index]];
      batch(() => {
        setFormCursor(index);
        // if (index === 0) {props.sizeContainerRef.scrollTop = 0;}
        //   else {           
        //     props.sizeContainerRef.style.overflowY = 'auto' 
        //     props.sizeContainerRef.scroll(0, element.offsetTop + 4);
        //     setTimeout(()=>{props.sizeContainerRef.style.overflowY = '';}, );
        // }
      })
    }
    else if (index == formSequence().length - 1) {
      setFormCursor(0);
    }

  }


  const validationData = () => {
    return measurements().map(measurement => measurementStatus()[measurement.id])
  }

  const handleUnitChange = () => {
    const targetUnit = currentUnit() === 'in' ? 'cm' : 'in'
    batch(() => {
      switchDisplayValueUnitsTo(targetUnit)
      toggleCurrentUnit()
    })
  }

  const handleHtmOpen = (id, status) => {
    setDiyData(prevState => {
      const newState = JSON.parse(JSON.stringify(prevState))
      Object.values(newState['measurements']).forEach(measurement => measurement['htmOpen'] = false);
      newState['measurements'][id]['htmOpen'] = status
      return newState
    })
  }

  const startBtnTexts = {
    [FORM_COMPLETION_STATUS.EMPTY]: 'Start',
    [FORM_COMPLETION_STATUS.HALF_COMPLETE]: 'Resume',
    [FORM_COMPLETION_STATUS.COMPLETE]: 'Review'
  }

  const closeAndResetCursor = () => {
    batch(() => {
      setFormCursor(0);
    })
  }

  return (
    <div class="flex gap-1 items-start">
      <ValidationStatus
        validationData={validationData()}
      />
      <div
        onClick={() => handleModalOpenState(true)}
        class="text-xs cursor-pointer  rounded-full text-ink-link whitespace-nowrap leading-none"
      >

        <svg aria-label="Edit icon" xmlns="http://www.w3.org/2000/svg" class="inline-block align-top leading-none" height="14" viewBox="0 -960 960 960" width="14"><path fill="currentColor" d="M216-216h44.461l393.463-393.463-44.461-44.461L216-260.461V-216Zm-51.999 51.999v-118.383L698-817.921l120.306 118.767-535.922 535.153H164.001ZM744-698.77 698.77-744 744-698.77Zm-112.697 67.467-21.84-22.621 44.461 44.461-22.621-21.84Z" /></svg>

        Edit

        {/* <ValidationBar
        validationData={validationData()}
        inline={true}
      /> */}
        <Show when={modalState()}>
          <ModalV2
            title={"Edit Measurements"}
            modalState={modalState()}
            handleModalOpenState={handleModalOpenState}
            closeButton={(fallback) => {
              return (
                <Show when={formCursor() > 0} fallback={fallback}>
                  <span onClick={closeAndResetCursor}
                    class="font-bold uppercase cursor-pointer absolute top-0 right-0 py-2 px-2 bg-canvas border-l-outer border-frame">✖ Close</span>
                </Show>
              )
            }}
          >
            <Suspense fallback={null}>
            <DIYFooterV2
              isSelected={formCursor() === 0}
              formDisplayValues={formDisplayValues}
              formDisplayCursor={formCursor}
              formDisplaySequence={formSequence}
              handleModalOpenState={handleModalOpenState}
              handleCursorChange={handleCursorChange}
              addFormViewReference={addFormViewReference} />
            <DIYBody
              addFormViewReference={addFormViewReference}
              formDisplaySequence={formSequence}
              formDisplayCursor={formCursor}
              formDisplayValues={formDisplayValues}
              handleCursorChange={handleCursorChange}
              diyData={diyData}
              measurements={measurementSchema()}
              unit={currentUnit()}
              measHtmVisibilities={measHtmVisibility()}
              measurementStatuses={measurementStatus()}
              ref={refObj}
              isKeyBoardOpen={props.isKeyBoardOpen}
              measurementsInfo={measurementsInfo}
              handleMeasurementChange={handleMeasurementChange}
              handleHtmVisibility={handleHtmVisibility}
              handleMeasurementStatusChange={handleMeasurementStatusChange}
              handleUnitChange={handleUnitChange}
              currentUnitSignal={currentUnit}
            />
            <Show when={formCursor() > 0 && formCursor() < formSequence().length - 1}>
              <VirtualKeyboard
                formDisplaySequence={formSequence}
                formDisplayCursor={formCursor}
                formDisplayValues={formDisplayValues}
                handleCursorChange={handleCursorChange}
                handleMeasurementChange={handleMeasurementChange}
              />
            </Show>
            </Suspense>
            {/* <Show when={formCursor() > 0}>
              <span onClick={closeAndResetCursor}
                class="font-bold uppercase cursor-pointer absolute top-0 right-0 py-2 px-2 bg-canvas border-l-outer border-frame">✖ Close</span>
            </Show> */}
          </ModalV2>
        </Show>
      </div>
    </div>
  )
}